import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PlayerViewMode from 'enum/PlayerViewMode';
import TypeResource from 'enum/TypeResource';
import TypeAction from 'enum/TypeAction';
import useUser from 'hooks/useUser';
import { useApiGet, useApiPost } from 'hooks/useApi';
import PlayerSettings from 'models/PlayerSettings';
import PlayerRemoteAction from 'models/PlayerRemoteAction';
import PlayerRemoteActionsTypes from 'enum/PlayerRemoteActionsTypes';
import useFieldSetErrors from 'hooks/useFieldSetErrors';
import { useToast } from 'hooks/useToast';

const yupSchemaPlayer = yup.object().shape({
  viewMode: yup
    .mixed<PlayerViewMode>()
    .oneOf(Object.values(PlayerViewMode))
    .required(),
  allowMove: yup.boolean().notRequired(),
  allowResize: yup.boolean().notRequired(),
  height: yup.string().notRequired(),
  width: yup.string().notRequired(),
  xPosition: yup.string().notRequired(),
  yPosition: yup.string().notRequired(),
  useProxy: yup.boolean().notRequired(),
  alwaysOnTop: yup.boolean().notRequired()
});

const useControllerPlayerSettingsPage = () => {
  const params = useParams();
  const user = useUser();
  const { addToast } = useToast();
  const [isLoadingFilesCache, setIsLoadingFilesCache] = useState(false);
  const [
    isLoadingFilesAndPlayerCache,
    setIsLoadingFilesAndPlayerCache
  ] = useState(false);
  const [isLoadingPlayerCache, setIsLoadingPlayerCache] = useState(false);

  const {
    mutate: mutatePlayerSettings,
    isPending: isPlayerSettings
  } = useApiPost<PlayerSettings>(`/device/${params.id}/settings`);

  const { mutate: mutatePlayerRemoteAction } = useApiPost<PlayerRemoteAction>(
    `/device/${params.id}/action`
  );

  const playerViewModeOptions = [
    { value: PlayerViewMode.FULL_SCREEN, label: 'Tela Cheia' },
    { value: PlayerViewMode.WINDOW, label: 'Janela' }
  ];

  const options = [
    { value: true, label: 'Sim' },
    { value: false, label: 'Não' }
  ];

  const userCanPerformAction = useMemo(() => {
    if (params.id) {
      return user?.resources?.[TypeResource.DEVICES]?.includes(
        TypeAction.UPDATE
      );
    } else {
      return user?.resources?.[TypeResource.DEVICES]?.includes(
        TypeAction.CREATE
      );
    }
  }, [user, params.id]);

  const formMethodsPlayer = useForm({
    resolver: yupResolver(yupSchemaPlayer)
  });

  const { data: currentPlayerSettings } = useApiGet<any>(
    `/device/${params.id}/settings`,
    {
      enabled: !!params.id
    }
  );

  const playerViewModeValue = useWatch({
    control: formMethodsPlayer.control,
    name: 'viewMode'
  });

  // alwaysOnTop is now independent of fullscreen — no longer forced to false

  useEffect(() => {
    if (!currentPlayerSettings) return;
    if (typeof (formMethodsPlayer as any)?.reset !== 'function') return;

    const toFormString = (value: unknown) => {
      if (value === null || value === undefined) return '';
      if (value === '') return '';
      return String(value);
    };

    (formMethodsPlayer as any).reset({
      viewMode: currentPlayerSettings.viewMode ?? PlayerViewMode.WINDOW,
      allowMove: !!currentPlayerSettings.allowMove,
      allowResize: !!currentPlayerSettings.allowResize,
      height: toFormString(currentPlayerSettings.height),
      width: toFormString(currentPlayerSettings.width),
      xPosition: toFormString(currentPlayerSettings.xPosition),
      yPosition: toFormString(currentPlayerSettings.yPosition),
      useProxy: !!currentPlayerSettings.useProxy,
      alwaysOnTop:
        typeof currentPlayerSettings.alwaysOnTop !== 'undefined'
          ? !!currentPlayerSettings.alwaysOnTop
          : false
    });
  }, [currentPlayerSettings, formMethodsPlayer]);

  const onError = useFieldSetErrors(formMethodsPlayer.setError);

  const handleOnSubmitPlayer = useCallback(
    (data: PlayerSettings) => {
      if (!userCanPerformAction) return;

      const toNullableInt = (value: unknown) => {
        if (value === '' || value === null || value === undefined) return null;
        const numberValue = Number(value);
        return Number.isFinite(numberValue) ? numberValue : null;
      };

      const viewMode = data.viewMode;
      const isWindowMode = viewMode === PlayerViewMode.WINDOW;

      const payload = isWindowMode
        ? ({
            viewMode,
            allowMove: !!data.allowMove,
            allowResize: !!data.allowResize,
            height: toNullableInt(data.height),
            width: toNullableInt(data.width),
            xPosition: toNullableInt(data.xPosition),
            yPosition: toNullableInt(data.yPosition),
            useProxy: !!data.useProxy,
            alwaysOnTop: !!data.alwaysOnTop
          } as any)
        : ({
            viewMode,
            allowMove: false,
            allowResize: false,
            height: null,
            width: null,
            xPosition: null,
            yPosition: null,
            useProxy: !!data.useProxy,
            alwaysOnTop: !!data.alwaysOnTop
          } as any);

      mutatePlayerSettings(payload, {
        onError,
        onSuccess: () => {
          addToast('success', 'Configurações do Player salvas com sucesso');
        }
      });
    },
    [userCanPerformAction, mutatePlayerSettings, onError, addToast]
  );

  const handleCacheAction = useCallback(
    (
      actionType: PlayerRemoteActionsTypes,
      successMessage: string,
      setLoading: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
      if (!userCanPerformAction) return;
      const dataPayload = {
        action: actionType,
        payload: null
      } as any;
      setLoading(true);
      mutatePlayerRemoteAction(dataPayload, {
        onError: (error) => {
          onError(error);
          setLoading(false);
        },
        onSuccess: () => {
          addToast('success', successMessage);
          setLoading(false);
        }
      });
    },
    [userCanPerformAction, mutatePlayerRemoteAction, onError, addToast]
  );

  return {
    formMethodsPlayer,
    handleOnSubmitPlayer,
    playerViewModeOptions,
    options,
    userCanPerformAction,
    handleCacheAction,
    isLoadingFilesCache,
    setIsLoadingFilesCache,
    isLoadingFilesAndPlayerCache,
    setIsLoadingFilesAndPlayerCache,
    isLoadingPlayerCache,
    setIsLoadingPlayerCache,
    playerViewModeValue,
    isPlayerSettings
  };
};

export default useControllerPlayerSettingsPage;
